/**
 * TopProgressBar.jsx — Fix #5a (2026-07-07).
 *
 * A slim (2 px) indigo progress bar docked to the very top of the app.
 * Automatically animates in on route transitions and out when the new
 * route commits. Never blocks user input. Sits above the Nav so it's
 * always visible even when a page is still fetching.
 *
 * Strategy:
 *  - Watches `useLocation()`. Any change starts a fresh animation cycle.
 *  - The bar animates from 0 → 90% in ~500 ms (asymptotic), holds there
 *    while the new route's initial render + first paint completes, then
 *    snaps to 100 % + fades out over ~200 ms.
 *  - CSS-only transitions — no external animation library needed.
 *  - Ignored on the first mount so a cold page load doesn't flash the bar.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const BAR_COLOR = "#1A237E"; // Brand indigo
const BAR_GLOW  = "rgba(26, 35, 126, 0.55)";

export default function TopProgressBar() {
    const location = useLocation();
    const [width, setWidth] = useState(0);
    const [opacity, setOpacity] = useState(0);
    const firstRenderRef = useRef(true);
    const holdTimerRef = useRef(null);
    const finishTimerRef = useRef(null);
    const fadeTimerRef = useRef(null);

    useEffect(() => {
        // Suppress on the initial mount so we don't flash the bar before
        // the user has done anything.
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        // Clear any in-flight timers so back-to-back navigations don't
        // stack their finish handlers.
        if (holdTimerRef.current)   clearTimeout(holdTimerRef.current);
        if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
        if (fadeTimerRef.current)   clearTimeout(fadeTimerRef.current);

        // Kick a fresh cycle: reset, then advance to 90% asymptotically.
        setOpacity(1);
        setWidth(6);
        // Advance to 90 % in ~500 ms
        holdTimerRef.current = setTimeout(() => setWidth(90), 40);
        // After ~700 ms of holding, race to 100 % and fade out.
        finishTimerRef.current = setTimeout(() => {
            setWidth(100);
            fadeTimerRef.current = setTimeout(() => {
                setOpacity(0);
                // Reset width so the next cycle starts from 0.
                setTimeout(() => setWidth(0), 220);
            }, 200);
        }, 700);

        return () => {
            if (holdTimerRef.current)   clearTimeout(holdTimerRef.current);
            if (finishTimerRef.current) clearTimeout(finishTimerRef.current);
            if (fadeTimerRef.current)   clearTimeout(fadeTimerRef.current);
        };
    }, [location.pathname]);

    return (
        <div
            aria-hidden="true"
            data-testid="top-progress-bar"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                zIndex: 9999,
                pointerEvents: "none",
                opacity,
                transition: "opacity 220ms ease-out",
            }}
        >
            <div
                style={{
                    height: "100%",
                    width: `${width}%`,
                    background: BAR_COLOR,
                    boxShadow: `0 0 8px 0 ${BAR_GLOW}`,
                    transition: width > 90
                        ? "width 200ms ease-out"
                        : "width 500ms cubic-bezier(0.1, 0.9, 0.2, 1)",
                }}
            />
        </div>
    );
}
